import { useNavigate } from 'react-router-dom'
import { lumi } from '../lib/lumi'
import {ArrowLeft, TrendingUp, Loader, FileBarChart} from 'lucide-react'
import { useState, useEffect } from 'react'

export default function AnaliseVendas() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [relatorio, setRelatorio] = useState('')
  const [propostas, setPropostas] = useState<any[]>([])
  const [contratos, setContratos] = useState<any[]>([])

  useEffect(() => {
    carregarDados()
  }, [])

  const carregarDados = async () => {
    try {
      const user = lumi.auth.user
      const userId = user?.userId || user?.id || user?._id

      if (!userId) return

      const [resultPropostas, resultContratos] = await Promise.all([
        lumi.entities.propostas.list({
          filter: { creator: userId },
          sort: { createdAt: -1 },
          limit: 1000
        }),
        lumi.entities.contratos.list({
          filter: { userId: userId },
          sort: { createdAt: -1 },
          limit: 1000
        })
      ])

      setPropostas(resultPropostas?.list || [])
      setContratos(resultContratos?.list || [])
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    }
  }

  const gerarAnalise = async () => {
    setLoading(true)
    try {
      const response = await fetch('https://p410083387467890688.lumi.page/analise-vendas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ propostas, contratos })
      })

      if (!response.ok) throw new Error('Erro na requisição')
      
      const data = await response.json()
      setRelatorio(data.content)
    } catch (error) {
      console.error('Erro ao gerar análise:', error)
      alert('Erro ao gerar análise. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="p-3 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#8B0000]">Análise Inteligente de Vendas</h1>
              <p className="text-sm text-gray-600">IA analisa seus dados e gera relatório estratégico</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Estatísticas Rápidas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-100">
            <div className="text-2xl font-bold text-gray-900">{propostas.length}</div>
            <div className="text-xs text-gray-600">Propostas Enviadas</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-100">
            <div className="text-2xl font-bold text-green-600">
              {propostas.filter(p => p.status === 'aceita').length}
            </div>
            <div className="text-xs text-gray-600">Propostas Aceitas</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-100">
            <div className="text-2xl font-bold text-[#8B0000]">
              {propostas.filter(p => p.status === 'perdida').length}
            </div>
            <div className="text-xs text-gray-600">Propostas Recusadas</div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-100">
            <div className="text-2xl font-bold text-blue-600">{contratos.length}</div>
            <div className="text-xs text-gray-600">Contratos Fechados</div>
          </div>
        </div>

        {/* Botão Gerar Análise */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp size={24} className="text-[#8B0000]" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Gerar Análise com IA</h2>
              <p className="text-sm text-gray-600">Receba insights estratégicos sobre suas vendas</p>
            </div>
          </div>
          
          <button
            onClick={gerarAnalise}
            disabled={loading || propostas.length === 0}
            className="w-full bg-[#8B0000] hover:bg-[#6B0000] text-white px-6 py-4 rounded-lg font-semibold transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader size={20} className="animate-spin" />
                Gerando análise...
              </>
            ) : (
              <>
                <FileBarChart size={20} />
                Gerar Relatório Estratégico
              </>
            )}
          </button>

          {propostas.length === 0 && (
            <p className="text-sm text-gray-500 text-center mt-3">
              Crie propostas para gerar sua primeira análise
            </p>
          )}
        </div>

        {/* Relatório */}
        {relatorio && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileBarChart size={22} className="text-[#8B0000]" />
              Relatório de Análise
            </h2>
            <div className="prose prose-sm max-w-none">
              <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {relatorio}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
